import React, { useState, useEffect, useRef } from 'react';
import { 
  LayoutDashboard, Package, ShoppingBag, Settings, 
  LogOut, Plus, Edit, Trash2, X, Image as ImageIcon,
  CheckCircle, XCircle, Truck, FileText, Upload, Star,
  Bell, DollarSign, Layers, ImageOff, AlertTriangle, RefreshCcw,
  Loader2, Database, List, Link as LinkIcon, Info, Wifi, ShieldAlert,
  ArrowRight, RefreshCw, Filter, Search, ChevronRight, Eye, Smartphone,
  BarChart3, Calendar, Tag, MessageSquare, Menu as MenuIcon, Sliders
} from 'lucide-react';
import { Product, Order, Banner, OrderStatus, Review, CategoryType, FlatDeal, TopCategory, PromoCode, StoreSettings, MenuItem } from '../types';
import { storage, db } from '../firebaseConfig';
import { ref, getDownloadURL, uploadString } from "firebase/storage";
import { collection, addDoc, getDocs, limit, query } from 'firebase/firestore';

// --- Sound Effect for New Orders ---
const playNotificationSound = () => {
    try {
        const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
        const oscillator = audioContext.createOscillator();
        const gainNode = audioContext.createGain();
        oscillator.connect(gainNode);
        gainNode.connect(audioContext.destination);
        oscillator.type = 'sine';
        oscillator.frequency.setValueAtTime(500, audioContext.currentTime);
        oscillator.frequency.exponentialRampToValueAtTime(1000, audioContext.currentTime + 0.1);
        gainNode.gain.setValueAtTime(0.1, audioContext.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);
        oscillator.start();
        oscillator.stop(audioContext.currentTime + 0.5);
    } catch (e) { console.error("Audio play failed", e); }
};

// --- Utility: Extreme Image Optimization ---
const optimizeImage = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = (event) => {
            const img = new Image();
            img.src = event.target?.result as string;
            img.onload = () => {
                const canvas = document.createElement('canvas');
                const MAX_WIDTH = 800; // Increased slightly for banners
                const MAX_HEIGHT = 800;
                let width = img.width;
                let height = img.height;

                if (width > height) {
                    if (width > MAX_WIDTH) {
                        height *= MAX_WIDTH / width;
                        width = MAX_WIDTH;
                    }
                } else {
                    if (height > MAX_HEIGHT) {
                        width *= MAX_HEIGHT / height;
                        height = MAX_HEIGHT;
                    }
                }
                
                canvas.width = width;
                canvas.height = height;
                const ctx = canvas.getContext('2d');
                ctx?.drawImage(img, 0, 0, width, height);
                const dataUrl = canvas.toDataURL('image/jpeg', 0.8);
                resolve(dataUrl);
            };
            img.onerror = (err) => reject(new Error("Image load failed"));
        };
        reader.onerror = (err) => reject(new Error("File read failed"));
    });
};

interface AdminDashboardProps {
  products: Product[];
  orders: Order[];
  banners: Banner[];
  reviews: Review[];
  categories: CategoryType[];
  flatDeals: FlatDeal[];
  topCategories: TopCategory[];
  promoCodes: PromoCode[];
  storeSettings: StoreSettings;
  menuItems: MenuItem[];
  onAddProduct: (p: Product) => Promise<void>;
  onUpdateProduct: (id: string, p: Partial<Product>) => Promise<void>;
  onDeleteProduct: (id: string) => Promise<void>;
  onUpdateOrder: (id: string, status: Partial<Order>) => Promise<void>;
  onAddCategory: (c: CategoryType) => Promise<void>;
  onDeleteCategory: (id: string) => Promise<void>;
  onAddBanner: (b: Banner) => Promise<void>;
  onDeleteBanner: (id: string) => Promise<void>;
  onAddFlatDeal: (d: FlatDeal) => Promise<void>;
  onDeleteFlatDeal: (id: string) => Promise<void>;
  onAddTopCategory: (t: TopCategory) => Promise<void>;
  onDeleteTopCategory: (id: string) => Promise<void>;
  onAddPromoCode: (p: PromoCode) => Promise<void>;
  onDeletePromoCode: (id: string) => Promise<void>;
  onUpdatePromoCode: (id: string, p: Partial<PromoCode>) => Promise<void>;
  onDeleteReview: (id: string) => Promise<void>;
  onUpdateStoreSettings: (id: string, s: Partial<StoreSettings>) => Promise<void>;
  onAddMenuItem: (m: MenuItem) => Promise<void>;
  onDeleteMenuItem: (id: string) => Promise<void>;
  onLogout: () => void;
  seedData: () => void;
  initialTab?: 'dashboard' | 'products' | 'categories' | 'orders' | 'layout' | 'reports' | 'promotions' | 'reviews' | 'settings';
}

export const AdminDashboard: React.FC<AdminDashboardProps> = ({
  products, orders, banners, reviews, categories, flatDeals, topCategories, promoCodes,
  storeSettings, menuItems,
  onAddProduct, onUpdateProduct, onDeleteProduct,
  onUpdateOrder, onAddCategory, onDeleteCategory,
  onAddBanner, onDeleteBanner, 
  onAddFlatDeal, onDeleteFlatDeal,
  onAddTopCategory, onDeleteTopCategory,
  onAddPromoCode, onDeletePromoCode, onUpdatePromoCode,
  onDeleteReview,
  onUpdateStoreSettings, onAddMenuItem, onDeleteMenuItem,
  onLogout, seedData, initialTab = 'orders'
}) => {
  const [activeTab, setActiveTab] = useState<'dashboard' | 'products' | 'categories' | 'orders' | 'layout' | 'reports' | 'promotions' | 'reviews' | 'settings'>(initialTab);
  
  useEffect(() => {
    if (initialTab) setActiveTab(initialTab);
  }, [initialTab]);

  // State for Real-time Notifications
  const [prevOrderCount, setPrevOrderCount] = useState(orders.length);
  const [newOrderAlert, setNewOrderAlert] = useState(false);

  // Dashboard Filter State
  const [dashboardTimeFilter, setDashboardTimeFilter] = useState<'all' | 'today' | 'week' | 'month'>('all');

  // Reports State
  const [reportStartDate, setReportStartDate] = useState(() => {
      const d = new Date();
      d.setDate(1); // 1st of current month
      return d.toISOString().split('T')[0];
  });
  const [reportEndDate, setReportEndDate] = useState(() => {
      return new Date().toISOString().split('T')[0];
  });

  // Order Management State
  const [orderFilter, setOrderFilter] = useState<OrderStatus | 'All'>('All');
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);

  // Product Management State
  const [isProductModalOpen, setIsProductModalOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Improved Image Upload State for Products
  const [selectedImages, setSelectedImages] = useState<File[]>([]);
  const [existingImages, setExistingImages] = useState<string[]>([]);
  const [imageUrlInput, setImageUrlInput] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // Layout Management State
  const [layoutSection, setLayoutSection] = useState<'banners' | 'flat_deals' | 'top_categories'>('banners');
  
  // -- Flat Deals State --
  const [newDeal, setNewDeal] = useState<Partial<FlatDeal>>({ price: '', label: '', bgColor: '#4a00e0' });
  
  // -- Top Categories State --
  const [newTopCat, setNewTopCat] = useState<Partial<TopCategory>>({ title: '', subtitle: '', linkCategory: 'Men' });
  const [topCatImage, setTopCatImage] = useState<File | null>(null);
  const topCatFileRef = useRef<HTMLInputElement>(null);
  const bannerFileRef = useRef<HTMLInputElement>(null);

  // -- Promo Code State --
  const [newPromo, setNewPromo] = useState<Partial<PromoCode>>({ code: '', type: 'percentage', value: 0, minOrderAmount: 0, isActive: true });

  // -- Settings State --
  const logoFileRef = useRef<HTMLInputElement>(null);
  const [newMenuItem, setNewMenuItem] = useState<Partial<MenuItem>>({ label: '', type: 'category', value: '', icon: 'Plus' });

  // --- Real-time Notification Logic ---
  const safeOrders = Array.isArray(orders) ? orders : [];
  useEffect(() => {
      if (safeOrders.length > prevOrderCount) {
          setNewOrderAlert(true);
          playNotificationSound();
          setTimeout(() => setNewOrderAlert(false), 5000);
      }
      setPrevOrderCount(safeOrders.length);
  }, [safeOrders.length]);

  // --- Helpers for Filtering ---
  const filterOrdersByTime = (orders: Order[], filter: 'all' | 'today' | 'week' | 'month') => {
      if (filter === 'all') return orders;
      const now = new Date();
      now.setHours(0,0,0,0);
      
      return orders.filter(o => {
          const d = new Date(o.date);
          d.setHours(0,0,0,0);
          if (filter === 'today') return d.getTime() === now.getTime();
          if (filter === 'week') {
              const weekAgo = new Date(now);
              weekAgo.setDate(weekAgo.getDate() - 7);
              return d >= weekAgo;
          }
          if (filter === 'month') {
              const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
              return d >= monthStart;
          }
          return true;
      });
  };

  const getFilteredReportOrders = () => {
      const start = new Date(reportStartDate);
      start.setHours(0,0,0,0);
      const end = new Date(reportEndDate);
      end.setHours(23,59,59,999);
      
      return safeOrders.filter(o => {
          const d = new Date(o.date);
          return d >= start && d <= end;
      });
  };

  // --- Product Form Logic ---
  const initialProductState = {
    id: '', name: '', description: '', price: 0, discountPrice: 0,
    category: categories.length > 0 ? categories[0].name : 'Men',
    stock: 0, sizes: '', colors: '', images: [] as string[], isActive: true
  };
  const [productForm, setProductForm] = useState(initialProductState);
  const [newCategoryName, setNewCategoryName] = useState('');

  // --- Handlers ---
  const handleEditProduct = (product: Product) => {
    setEditingProduct(product);
    setProductForm({
      ...product,
      discountPrice: product.discountPrice || 0,
      sizes: Array.isArray(product.sizes) ? product.sizes.join(', ') : product.sizes,
      colors: Array.isArray(product.colors) ? product.colors.join(', ') : product.colors,
    });
    setExistingImages(product.images || []);
    setSelectedImages([]);
    setImageUrlInput('');
    setIsProductModalOpen(true);
  };

  const handleAddProductClick = () => {
    setEditingProduct(null);
    setProductForm(initialProductState);
    setExistingImages([]);
    setSelectedImages([]);
    setImageUrlInput('');
    setIsProductModalOpen(true);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const newFiles = Array.from(e.target.files);
      setSelectedImages(prev => [...prev, ...newFiles]);
    }
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const processImageForSave = async (file: File): Promise<string> => {
      const base64String = await optimizeImage(file);
      if (base64String.length < 400000) return base64String;
      try {
          const fileName = `uploads/${Date.now()}_${Math.random().toString(36).substring(7)}.jpg`;
          const storageRef = ref(storage, fileName);
          await uploadString(storageRef, base64String, 'data_url');
          return await getDownloadURL(storageRef);
      } catch (err) {
          return base64String;
      }
  };

  const saveProduct = async (e: React.FormEvent) => {
    e?.preventDefault();
    if (isSubmitting) return;
    setIsSubmitting(true);

    try {
        const newImageUrls: string[] = [];
        for (const file of selectedImages) {
            newImageUrls.push(await processImageForSave(file));
        }

        const finalImages = [...existingImages, ...newImageUrls];
        if (finalImages.length === 0) {
            alert("Please add at least one image.");
            setIsSubmitting(false);
            return;
        }
        
        const payload: Product = {
            id: editingProduct ? editingProduct.id : Date.now().toString(),
            name: productForm.name || 'New Product',
            description: productForm.description || '',
            price: productForm.price || 0,
            discountPrice: productForm.discountPrice,
            category: productForm.category,
            stock: productForm.stock || 0,
            images: finalImages,
            isActive: productForm.isActive,
            sizes: typeof productForm.sizes === 'string' ? productForm.sizes.split(',').map(s=>s.trim()).filter(Boolean) : productForm.sizes,
            colors: typeof productForm.colors === 'string' ? productForm.colors.split(',').map(s=>s.trim()).filter(Boolean) : productForm.colors
        };

        if (editingProduct) await onUpdateProduct(editingProduct.id, payload);
        else await onAddProduct(payload);
        
        setIsProductModalOpen(false);
    } catch (error: any) {
        alert("Error saving: " + error.message);
    } finally {
        setIsSubmitting(false);
    }
  };

  // --- Layout Handlers ---
  const handleAddBanner = async (e: React.ChangeEvent<HTMLInputElement>) => {
      if (!e.target.files || !e.target.files[0]) return;
      setIsSubmitting(true);
      try {
          const url = await processImageForSave(e.target.files[0]);
          await onAddBanner({ id: Date.now().toString(), imageUrl: url, title: '' });
      } catch(e) { alert("Upload failed"); }
      finally { setIsSubmitting(false); if(bannerFileRef.current) bannerFileRef.current.value = ''; }
  };

  const handleSaveFlatDeal = async () => {
      if(!newDeal.price || !newDeal.label) return;
      await onAddFlatDeal({ 
          id: Date.now().toString(), 
          price: newDeal.price, 
          label: newDeal.label, 
          bgColor: newDeal.bgColor || '#4a00e0' 
      });
      setNewDeal({ price: '', label: '', bgColor: '#4a00e0' });
  };

  const handleSaveTopCategory = async () => {
      if(!newTopCat.title || !newTopCat.subtitle || !topCatImage) {
          alert("Please fill all fields and select an image.");
          return;
      }
      setIsSubmitting(true);
      try {
          const url = await processImageForSave(topCatImage);
          await onAddTopCategory({
              id: Date.now().toString(),
              title: newTopCat.title,
              subtitle: newTopCat.subtitle,
              linkCategory: newTopCat.linkCategory || 'Men',
              imageUrl: url
          });
          setNewTopCat({ title: '', subtitle: '', linkCategory: 'Men' });
          setTopCatImage(null);
          if(topCatFileRef.current) topCatFileRef.current.value = '';
      } catch(e) { alert("Failed"); }
      finally { setIsSubmitting(false); }
  };

  // --- Promo Handlers ---
  const handleAddPromo = async () => {
      if(!newPromo.code || !newPromo.value) return;
      await onAddPromoCode({
          id: Date.now().toString(),
          code: newPromo.code.toUpperCase(),
          type: newPromo.type || 'percentage',
          value: Number(newPromo.value),
          minOrderAmount: Number(newPromo.minOrderAmount || 0),
          isActive: true
      });
      setNewPromo({ code: '', type: 'percentage', value: 0, minOrderAmount: 0, isActive: true });
  };

  // --- Settings Handlers ---
  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
      if (!e.target.files || !e.target.files[0]) return;
      setIsSubmitting(true);
      try {
          const url = await processImageForSave(e.target.files[0]);
          await onUpdateStoreSettings('general', { logoUrl: url });
      } catch(e) { alert("Logo upload failed"); }
      finally { setIsSubmitting(false); if(logoFileRef.current) logoFileRef.current.value = ''; }
  };

  const handleAddMenuItem = async () => {
      if (!newMenuItem.label || !newMenuItem.value) return;
      await onAddMenuItem({
          id: Date.now().toString(),
          label: newMenuItem.label,
          type: newMenuItem.type || 'category',
          value: newMenuItem.value,
          icon: newMenuItem.icon || 'Plus',
          tag: newMenuItem.tag
      });
      setNewMenuItem({ label: '', type: 'category', value: '', icon: 'Plus', tag: '' });
  };

  // --- Render Sections ---

  const renderLayoutEditor = () => (
      <div className="flex gap-6">
          {/* Sub Sidebar */}
          <div className="w-48 bg-white rounded-xl shadow-sm border border-gray-200 p-2 h-fit">
              <button onClick={()=>setLayoutSection('banners')} className={`w-full text-left px-4 py-2 rounded-lg mb-1 ${layoutSection==='banners' ? 'bg-black text-white' : 'hover:bg-gray-50'}`}>Hero Sliders</button>
              <button onClick={()=>setLayoutSection('flat_deals')} className={`w-full text-left px-4 py-2 rounded-lg mb-1 ${layoutSection==='flat_deals' ? 'bg-black text-white' : 'hover:bg-gray-50'}`}>Flat Deals</button>
              <button onClick={()=>setLayoutSection('top_categories')} className={`w-full text-left px-4 py-2 rounded-lg ${layoutSection==='top_categories' ? 'bg-black text-white' : 'hover:bg-gray-50'}`}>Top Categories</button>
          </div>

          <div className="flex-1 space-y-6">
              {/* SLIDERS EDITOR */}
              {layoutSection === 'banners' && (
                  <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
                      <div className="flex justify-between items-center mb-4">
                          <h3 className="font-bold text-lg">Home Page Sliders (Promo Images)</h3>
                          <label className="bg-black text-white px-4 py-2 rounded-lg cursor-pointer flex items-center gap-2 hover:bg-gray-800">
                              {isSubmitting ? <Loader2 className="animate-spin" size={16}/> : <Plus size={16}/>}
                              Add New Slide
                              <input ref={bannerFileRef} type="file" className="hidden" accept="image/*" onChange={handleAddBanner} disabled={isSubmitting}/>
                          </label>
                      </div>
                      <p className="text-sm text-gray-500 mb-4">These images appear at the top of the home page. Use these for major promotions.</p>
                      <div className="grid grid-cols-1 gap-4">
                          {banners.map(b => (
                              <div key={b.id} className="relative group rounded-xl overflow-hidden aspect-[3/1] bg-gray-100">
                                  <img src={b.imageUrl} className="w-full h-full object-cover" />
                                  <button onClick={()=>onDeleteBanner(b.id)} className="absolute top-2 right-2 bg-red-600 text-white p-2 rounded-full shadow-lg opacity-0 group-hover:opacity-100 transition-opacity">
                                      <Trash2 size={20}/>
                                  </button>
                              </div>
                          ))}
                      </div>
                  </div>
              )}

              {/* FLAT DEALS EDITOR */}
              {layoutSection === 'flat_deals' && (
                  <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
                      <h3 className="font-bold text-lg mb-4">Flat Deal Boxes</h3>
                      <div className="flex gap-2 mb-6 p-4 bg-gray-50 rounded-xl items-end">
                          <div>
                              <label className="text-xs font-bold text-gray-500">Label (e.g. Everything)</label>
                              <input className="border p-2 rounded w-full" value={newDeal.label} onChange={e=>setNewDeal({...newDeal, label: e.target.value})} placeholder="Label"/>
                          </div>
                          <div>
                              <label className="text-xs font-bold text-gray-500">Price (e.g. 2,999)</label>
                              <input className="border p-2 rounded w-full" value={newDeal.price} onChange={e=>setNewDeal({...newDeal, price: e.target.value})} placeholder="Price"/>
                          </div>
                          <div>
                              <label className="text-xs font-bold text-gray-500">Color</label>
                              <input type="color" className="h-10 w-full cursor-pointer" value={newDeal.bgColor} onChange={e=>setNewDeal({...newDeal, bgColor: e.target.value})} />
                          </div>
                          <button onClick={handleSaveFlatDeal} className="bg-black text-white px-4 py-2 rounded h-10 font-bold hover:bg-gray-800">Add</button>
                      </div>

                      <div className="grid grid-cols-4 gap-4">
                          {flatDeals.map(d => (
                              <div key={d.id} className="relative p-4 rounded-lg text-white text-center flex flex-col justify-center h-24" style={{backgroundColor: d.bgColor}}>
                                  <span className="text-xs uppercase">{d.label}</span>
                                  <span className="font-bold text-xl">Rs {d.price}</span>
                                  <button onClick={()=>onDeleteFlatDeal(d.id)} className="absolute -top-2 -right-2 bg-red-600 rounded-full p-1 shadow-md hover:scale-110 transition-transform">
                                      <X size={12} />
                                  </button>
                              </div>
                          ))}
                      </div>
                  </div>
              )}

              {/* TOP CATEGORIES EDITOR */}
              {layoutSection === 'top_categories' && (
                   <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
                      <h3 className="font-bold text-lg mb-4">Top Category Cards</h3>
                      <p className="text-sm text-gray-500 mb-4">Images for the main category sections on the home page.</p>
                      <div className="grid grid-cols-2 gap-4 mb-6 p-4 bg-gray-50 rounded-xl">
                          <div><input className="border p-2 rounded w-full mb-2" value={newTopCat.title} onChange={e=>setNewTopCat({...newTopCat, title: e.target.value})} placeholder="Title (e.g. MEN)"/></div>
                          <div><input className="border p-2 rounded w-full mb-2" value={newTopCat.subtitle} onChange={e=>setNewTopCat({...newTopCat, subtitle: e.target.value})} placeholder="Subtitle (e.g. UPTO 80% OFF)"/></div>
                          <div>
                              <select className="border p-2 rounded w-full" value={newTopCat.linkCategory} onChange={e=>setNewTopCat({...newTopCat, linkCategory: e.target.value})}>
                                  {categories.map(c=><option key={c.id} value={c.name}>{c.name}</option>)}
                              </select>
                          </div>
                          <div><input ref={topCatFileRef} type="file" onChange={e => setTopCatImage(e.target.files?.[0] || null)} className="border p-1 rounded w-full bg-white"/></div>
                          <button onClick={handleSaveTopCategory} disabled={isSubmitting} className="col-span-2 bg-black text-white py-2 rounded font-bold hover:bg-gray-800 flex justify-center">{isSubmitting ? <Loader2 className="animate-spin"/> : 'Add Category Card'}</button>
                      </div>

                      <div className="grid grid-cols-3 gap-4">
                          {topCategories.map(c => (
                              <div key={c.id} className="relative h-64 bg-[#4a00e0] overflow-hidden rounded-lg group">
                                  <div className="p-3 text-white z-10 relative">
                                     <h3 className="font-bold text-lg leading-tight uppercase">{c.title}</h3>
                                     <div className="bg-yellow-400 text-black text-[10px] font-bold px-1 py-0.5 inline-block mt-1 uppercase">{c.subtitle}</div>
                                  </div>
                                  <img src={c.imageUrl} className="absolute bottom-0 right-0 w-4/5 object-contain opacity-80" />
                                  <button onClick={()=>onDeleteTopCategory(c.id)} className="absolute top-2 right-2 bg-red-600 text-white p-1 rounded z-20 hover:bg-red-700">Delete</button>
                              </div>
                          ))}
                      </div>
                   </div>
              )}
          </div>
      </div>
  );

  const pendingOrdersCount = safeOrders.filter(o => o.status === 'Pending').length;

  return (
    <div className="flex min-h-screen bg-gray-100 font-sans">
      {/* Sidebar */}
      <aside className="w-64 bg-white border-r border-gray-200 min-h-screen fixed left-0 top-0 z-10 flex flex-col">
        <div className="p-6 border-b border-gray-100">
          <h1 className="text-2xl font-black text-black tracking-tighter">SHOEBHAI<span className="text-blue-600">.</span></h1>
          <p className="text-xs text-gray-400 font-medium uppercase tracking-widest mt-1">Admin Panel</p>
        </div>
        
        <nav className="flex-1 px-4 py-6 space-y-1">
          <button onClick={() => setActiveTab('dashboard')} className={`w-full flex items-center justify-between px-4 py-3 rounded-xl text-sm font-medium transition-colors ${activeTab === 'dashboard' ? 'bg-gray-100 text-black' : 'text-gray-500 hover:bg-gray-50'}`}>
             <span className="flex items-center gap-3"><LayoutDashboard size={20} /> Dashboard</span>
          </button>
          
          <button onClick={() => setActiveTab('orders')} className={`w-full flex items-center justify-between px-4 py-3 rounded-xl text-sm font-medium transition-colors ${activeTab === 'orders' ? 'bg-blue-50 text-blue-600' : 'text-gray-500 hover:bg-gray-50'}`}>
             <span className="flex items-center gap-3"><Package size={20} /> Orders</span>
             {pendingOrdersCount > 0 && <span className="bg-red-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">{pendingOrdersCount}</span>}
          </button>
          
          <button onClick={() => setActiveTab('products')} className={`w-full flex items-center justify-between px-4 py-3 rounded-xl text-sm font-medium transition-colors ${activeTab === 'products' ? 'bg-gray-100 text-black' : 'text-gray-500 hover:bg-gray-50'}`}>
             <span className="flex items-center gap-3"><ShoppingBag size={20} /> Products</span>
          </button>
          
          <button onClick={() => setActiveTab('reports')} className={`w-full flex items-center justify-between px-4 py-3 rounded-xl text-sm font-medium transition-colors ${activeTab === 'reports' ? 'bg-gray-100 text-black' : 'text-gray-500 hover:bg-gray-50'}`}>
             <span className="flex items-center gap-3"><BarChart3 size={20} /> Sales Reports</span>
          </button>
          
          <button onClick={() => setActiveTab('layout')} className={`w-full flex items-center justify-between px-4 py-3 rounded-xl text-sm font-medium transition-colors ${activeTab === 'layout' ? 'bg-gray-100 text-black' : 'text-gray-500 hover:bg-gray-50'}`}>
             <span className="flex items-center gap-3"><ImageIcon size={20} /> Layout & Promo Images</span>
          </button>

           <button onClick={() => setActiveTab('promotions')} className={`w-full flex items-center justify-between px-4 py-3 rounded-xl text-sm font-medium transition-colors ${activeTab === 'promotions' ? 'bg-gray-100 text-black' : 'text-gray-500 hover:bg-gray-50'}`}>
             <span className="flex items-center gap-3"><Tag size={20} /> Promotions</span>
          </button>

          <button onClick={() => setActiveTab('reviews')} className={`w-full flex items-center justify-between px-4 py-3 rounded-xl text-sm font-medium transition-colors ${activeTab === 'reviews' ? 'bg-gray-100 text-black' : 'text-gray-500 hover:bg-gray-50'}`}>
             <span className="flex items-center gap-3"><MessageSquare size={20} /> Customer Reviews</span>
          </button>

           <button onClick={() => setActiveTab('categories')} className={`w-full flex items-center justify-between px-4 py-3 rounded-xl text-sm font-medium transition-colors ${activeTab === 'categories' ? 'bg-gray-100 text-black' : 'text-gray-500 hover:bg-gray-50'}`}>
             <span className="flex items-center gap-3"><List size={20} /> Categories</span>
          </button>

          <button onClick={() => setActiveTab('settings')} className={`w-full flex items-center justify-between px-4 py-3 rounded-xl text-sm font-medium transition-colors ${activeTab === 'settings' ? 'bg-gray-100 text-black' : 'text-gray-500 hover:bg-gray-50'}`}>
             <span className="flex items-center gap-3"><Sliders size={20} /> Store Settings</span>
          </button>
        </nav>

        <div className="p-4 border-t border-gray-100">
           <button onClick={onLogout} className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium text-red-600 hover:bg-red-50 transition-colors">
               <LogOut size={20} /> Logout
           </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="ml-64 flex-1 p-8">
        
        {/* Top Header */}
        <div className="flex justify-between items-center mb-8">
             <div>
                 <h1 className="text-2xl font-bold text-gray-900 capitalize">{activeTab === 'layout' ? 'Layout & Promo Images' : activeTab.replace('_', ' ')}</h1>
                 <p className="text-sm text-gray-500">Welcome back, Admin</p>
             </div>
             <div className="flex items-center gap-4">
                 <button className="relative p-2 bg-white rounded-full shadow-sm hover:bg-gray-50 border border-gray-200">
                     <Bell size={20} className="text-gray-600" />
                     {newOrderAlert && <span className="absolute top-0 right-0 w-3 h-3 bg-red-500 rounded-full animate-ping"></span>}
                     {pendingOrdersCount > 0 && <span className="absolute top-0 right-0 w-3 h-3 bg-red-500 rounded-full border-2 border-white"></span>}
                 </button>
             </div>
        </div>

        {activeTab === 'settings' && (
            <div className="space-y-8">
                {/* LOGO SETTINGS */}
                <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
                    <h3 className="font-bold text-lg mb-4">Store Branding</h3>
                    <div className="flex items-center gap-6">
                         <div className="w-32 h-32 border-2 border-dashed rounded-xl flex items-center justify-center bg-gray-50 overflow-hidden">
                             {storeSettings.logoUrl ? (
                                 <img src={storeSettings.logoUrl} className="w-full h-full object-contain" alt="Logo" />
                             ) : (
                                 <span className="text-gray-400 text-xs">No Logo</span>
                             )}
                         </div>
                         <div>
                             <p className="text-sm font-bold mb-2">Upload Logo</p>
                             <label className="bg-black text-white px-4 py-2 rounded-lg cursor-pointer inline-flex items-center gap-2 hover:bg-gray-800">
                                 {isSubmitting ? <Loader2 className="animate-spin" size={16}/> : <Upload size={16}/>}
                                 Choose File
                                 <input ref={logoFileRef} type="file" className="hidden" accept="image/*" onChange={handleLogoUpload} disabled={isSubmitting}/>
                             </label>
                             <p className="text-xs text-gray-500 mt-2">Recommended size: 200x60px. Transparent PNG.</p>
                         </div>
                    </div>
                </div>

                {/* MENU EDITOR */}
                <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
                    <h3 className="font-bold text-lg mb-4">Sidebar Menu Management</h3>
                    
                    {/* Add Item Form */}
                    <div className="grid grid-cols-5 gap-3 mb-6 p-4 bg-gray-50 rounded-xl items-end">
                        <div className="col-span-1">
                            <label className="text-xs font-bold uppercase text-gray-500 mb-1">Label</label>
                            <input className="w-full border p-2 rounded-lg" placeholder="Menu Item Name" value={newMenuItem.label} onChange={e=>setNewMenuItem({...newMenuItem, label: e.target.value})}/>
                        </div>
                        <div className="col-span-1">
                             <label className="text-xs font-bold uppercase text-gray-500 mb-1">Type</label>
                             <select className="w-full border p-2 rounded-lg" value={newMenuItem.type} onChange={e=>setNewMenuItem({...newMenuItem, type: e.target.value as any, value: ''})}>
                                 <option value="category">Category Link</option>
                                 <option value="action">System Action</option>
                                 <option value="link">External URL</option>
                             </select>
                        </div>
                        <div className="col-span-1">
                             <label className="text-xs font-bold uppercase text-gray-500 mb-1">Target / Value</label>
                             {newMenuItem.type === 'action' ? (
                                 <select className="w-full border p-2 rounded-lg" value={newMenuItem.value} onChange={e=>setNewMenuItem({...newMenuItem, value: e.target.value})}>
                                     <option value="">Select Action...</option>
                                     <option value="login">Login / Register</option>
                                     <option value="track_order">Track Order</option>
                                 </select>
                             ) : newMenuItem.type === 'category' ? (
                                 <select className="w-full border p-2 rounded-lg" value={newMenuItem.value} onChange={e=>setNewMenuItem({...newMenuItem, value: e.target.value})}>
                                     <option value="">Select Category...</option>
                                     {categories.map(c=><option key={c.id} value={c.name}>{c.name}</option>)}
                                     <option value="All">All Products</option>
                                     <option value="Sale">Sale</option>
                                     <option value="Returns">Returns</option>
                                 </select>
                             ) : (
                                 <input className="w-full border p-2 rounded-lg" placeholder="https://..." value={newMenuItem.value} onChange={e=>setNewMenuItem({...newMenuItem, value: e.target.value})}/>
                             )}
                        </div>
                        <div className="col-span-1">
                            <label className="text-xs font-bold uppercase text-gray-500 mb-1">Icon</label>
                            <select className="w-full border p-2 rounded-lg" value={newMenuItem.icon} onChange={e=>setNewMenuItem({...newMenuItem, icon: e.target.value})}>
                                {['Plus', 'User', 'Package', 'MapPin', 'Phone', 'Mail', 'Lock', 'Box'].map(i => <option key={i} value={i}>{i}</option>)}
                            </select>
                        </div>
                        <button onClick={handleAddMenuItem} className="bg-blue-600 text-white p-2 rounded-lg font-bold h-[42px] hover:bg-blue-700">Add Item</button>
                    </div>

                    {/* Menu List */}
                    <div className="overflow-hidden rounded-lg border">
                        <table className="min-w-full divide-y divide-gray-200">
                             <thead className="bg-gray-50">
                                 <tr>
                                     <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase">Label</th>
                                     <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase">Type</th>
                                     <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase">Target</th>
                                     <th className="px-6 py-3 text-right text-xs font-bold text-gray-500 uppercase">Action</th>
                                 </tr>
                             </thead>
                             <tbody className="bg-white divide-y divide-gray-200">
                                 {menuItems.map(item => (
                                     <tr key={item.id}>
                                         <td className="px-6 py-4 font-medium flex items-center gap-2">
                                             <span className="bg-gray-100 text-gray-500 p-1 rounded text-[10px]">{item.icon}</span>
                                             {item.label}
                                         </td>
                                         <td className="px-6 py-4 text-sm text-gray-500 capitalize">{item.type}</td>
                                         <td className="px-6 py-4 text-sm font-mono text-blue-600">{item.value}</td>
                                         <td className="px-6 py-4 text-right">
                                             <button onClick={() => onDeleteMenuItem(item.id)} className="text-red-500 hover:text-red-700 p-2 hover:bg-red-50 rounded">
                                                 <Trash2 size={16}/>
                                             </button>
                                         </td>
                                     </tr>
                                 ))}
                             </tbody>
                        </table>
                    </div>
                </div>
            </div>
        )}

        {activeTab === 'layout' && renderLayoutEditor()}

        {activeTab === 'promotions' && (
             <div className="space-y-6">
                 <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
                     <h3 className="font-bold text-lg mb-4">Create New Promotion</h3>
                     <div className="grid grid-cols-4 gap-4 items-end bg-gray-50 p-4 rounded-xl">
                         <div>
                             <label className="text-xs font-bold uppercase text-gray-500 mb-1">Coupon Code</label>
                             <input className="w-full border p-2 rounded-lg" placeholder="e.g. SALE50" value={newPromo.code} onChange={e=>setNewPromo({...newPromo, code: e.target.value})}/>
                         </div>
                         <div>
                             <label className="text-xs font-bold uppercase text-gray-500 mb-1">Type</label>
                             <select className="w-full border p-2 rounded-lg" value={newPromo.type} onChange={e=>setNewPromo({...newPromo, type: e.target.value as any})}>
                                 <option value="percentage">Percentage (%)</option>
                                 <option value="fixed">Fixed Amount (Rs)</option>
                             </select>
                         </div>
                         <div>
                             <label className="text-xs font-bold uppercase text-gray-500 mb-1">Value</label>
                             <input type="number" className="w-full border p-2 rounded-lg" placeholder="e.g. 10" value={newPromo.value} onChange={e=>setNewPromo({...newPromo, value: Number(e.target.value)})}/>
                         </div>
                         <button onClick={handleAddPromo} className="bg-black text-white p-2 rounded-lg font-bold h-[42px]">Add Coupon</button>
                     </div>
                 </div>

                 <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                     <table className="min-w-full divide-y divide-gray-200">
                         <thead className="bg-gray-50">
                             <tr>
                                 <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase">Code</th>
                                 <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase">Discount</th>
                                 <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase">Status</th>
                                 <th className="px-6 py-3 text-right text-xs font-bold text-gray-500 uppercase">Action</th>
                             </tr>
                         </thead>
                         <tbody className="bg-white divide-y divide-gray-200">
                             {promoCodes.map(promo => (
                                 <tr key={promo.id}>
                                     <td className="px-6 py-4 font-bold text-blue-600 tracking-wider">{promo.code}</td>
                                     <td className="px-6 py-4 font-medium">
                                         {promo.type === 'percentage' ? `${promo.value}% OFF` : `Rs. ${promo.value} OFF`}
                                     </td>
                                     <td className="px-6 py-4">
                                         <button onClick={() => onUpdatePromoCode(promo.id, { isActive: !promo.isActive })} className={`px-2 py-1 rounded text-xs font-bold ${promo.isActive ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                                             {promo.isActive ? 'Active' : 'Inactive'}
                                         </button>
                                     </td>
                                     <td className="px-6 py-4 text-right">
                                         <button onClick={() => onDeletePromoCode(promo.id)} className="text-red-500 hover:text-red-700"><Trash2 size={18}/></button>
                                     </td>
                                 </tr>
                             ))}
                         </tbody>
                     </table>
                 </div>
             </div>
        )}

        {activeTab === 'reviews' && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                <div className="p-6 border-b bg-gray-50 flex justify-between items-center">
                    <h3 className="font-bold text-gray-800">Customer Reviews</h3>
                    <span className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full font-bold">{reviews.length} Total</span>
                </div>
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-white">
                        <tr>
                            <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase">Date</th>
                            <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase">Customer</th>
                            <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase">Product</th>
                            <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase">Rating</th>
                            <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase">Comment</th>
                            <th className="px-6 py-3 text-right text-xs font-bold text-gray-500 uppercase">Action</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 bg-white">
                        {reviews.length === 0 ? (
                            <tr>
                                <td colSpan={6} className="px-6 py-10 text-center text-gray-500">No reviews yet.</td>
                            </tr>
                        ) : (
                            reviews.sort((a,b)=>new Date(b.date).getTime() - new Date(a.date).getTime()).map(review => {
                                const product = products.find(p => p.id === review.productId);
                                return (
                                    <tr key={review.id} className="hover:bg-gray-50">
                                        <td className="px-6 py-4 text-xs text-gray-500">{new Date(review.date).toLocaleDateString()}</td>
                                        <td className="px-6 py-4 text-sm font-medium">{review.customerName}</td>
                                        <td className="px-6 py-4 text-sm text-gray-600">{product?.name || 'Unknown Product'}</td>
                                        <td className="px-6 py-4">
                                            <div className="flex text-yellow-400">
                                                {[...Array(5)].map((_, i) => (
                                                    <Star key={i} size={14} fill={i < review.rating ? "currentColor" : "none"} stroke="currentColor" />
                                                ))}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 text-sm text-gray-600 max-w-xs truncate">{review.comment}</td>
                                        <td className="px-6 py-4 text-right">
                                            <button onClick={() => onDeleteReview(review.id)} className="text-red-500 hover:text-red-700 hover:bg-red-50 p-2 rounded">
                                                <Trash2 size={16}/>
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })
                        )}
                    </tbody>
                </table>
            </div>
        )}

        {activeTab === 'reports' && (
           <div className="space-y-6">
               <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
                   <h2 className="text-lg font-bold mb-4 flex items-center gap-2"><Filter size={20}/> Generate Report</h2>
                   <div className="flex items-end gap-4">
                       <div className="flex-1">
                           <label className="block text-xs font-bold uppercase text-gray-500 mb-1">From</label>
                           <input type="date" value={reportStartDate} onChange={e=>setReportStartDate(e.target.value)} className="w-full border p-2 rounded-lg"/>
                       </div>
                       <div className="flex-1">
                           <label className="block text-xs font-bold uppercase text-gray-500 mb-1">To</label>
                           <input type="date" value={reportEndDate} onChange={e=>setReportEndDate(e.target.value)} className="w-full border p-2 rounded-lg"/>
                       </div>
                       <button className="bg-black text-white px-6 py-2.5 rounded-lg font-bold hover:bg-gray-800">Refresh Data</button>
                   </div>
               </div>

               {/* Report Cards */}
               <div className="grid grid-cols-4 gap-4">
                    <div className="bg-white p-5 rounded-xl border border-blue-100 bg-blue-50/50">
                        <p className="text-sm text-gray-500 font-bold">Total Sales</p>
                        <p className="text-2xl font-bold text-blue-700">Rs. {getFilteredReportOrders().reduce((acc,o)=>acc+(o.total||0),0).toLocaleString()}</p>
                    </div>
                    <div className="bg-white p-5 rounded-xl border border-green-100 bg-green-50/50">
                        <p className="text-sm text-gray-500 font-bold">Orders Count</p>
                        <p className="text-2xl font-bold text-green-700">{getFilteredReportOrders().length}</p>
                    </div>
                    <div className="bg-white p-5 rounded-xl border border-purple-100 bg-purple-50/50">
                        <p className="text-sm text-gray-500 font-bold">Avg Order Value</p>
                        <p className="text-2xl font-bold text-purple-700">
                            Rs. {getFilteredReportOrders().length ? Math.round(getFilteredReportOrders().reduce((acc,o)=>acc+(o.total||0),0)/getFilteredReportOrders().length).toLocaleString() : 0}
                        </p>
                    </div>
                    <div className="bg-white p-5 rounded-xl border border-red-100 bg-red-50/50">
                        <p className="text-sm text-gray-500 font-bold">Cancelled</p>
                        <p className="text-2xl font-bold text-red-700">{getFilteredReportOrders().filter(o=>o.status==='Declined').length}</p>
                    </div>
               </div>

               {/* Report Table */}
               <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                   <div className="p-4 border-b bg-gray-50 font-bold text-gray-700">Detailed Transaction Log</div>
                   <table className="min-w-full divide-y divide-gray-200">
                       <thead className="bg-gray-50">
                           <tr>
                               <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase">Date</th>
                               <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase">Order ID</th>
                               <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase">Customer</th>
                               <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase">Status</th>
                               <th className="px-6 py-3 text-right text-xs font-bold text-gray-500 uppercase">Amount</th>
                           </tr>
                       </thead>
                       <tbody className="bg-white divide-y divide-gray-200">
                           {getFilteredReportOrders().map(o => (
                               <tr key={o.id}>
                                   <td className="px-6 py-4 text-sm text-gray-600">{new Date(o.date).toLocaleDateString()}</td>
                                   <td className="px-6 py-4 text-sm font-bold text-blue-600">#{o.id}</td>
                                   <td className="px-6 py-4 text-sm text-gray-800">{o.customerName}</td>
                                   <td className="px-6 py-4 text-sm"><span className={`px-2 py-1 rounded text-xs font-bold ${o.status==='Delivered'?'bg-green-100 text-green-800':'bg-gray-100'}`}>{o.status}</span></td>
                                   <td className="px-6 py-4 text-sm text-right font-bold">Rs. {o.total.toLocaleString()}</td>
                               </tr>
                           ))}
                       </tbody>
                   </table>
               </div>
           </div>
        )}

        {activeTab === 'orders' && (
           <div className="space-y-6">
            <div className="flex justify-between items-center">
                <h2 className="text-2xl font-bold text-gray-800">Order Management</h2>
                <div className="flex gap-2">
                    {['All', 'Pending', 'Approved', 'Shipped', 'Delivered', 'Declined'].map(status => (
                        <button
                            key={status}
                            onClick={() => setOrderFilter(status as any)}
                            className={`px-4 py-2 rounded-lg text-sm font-bold transition-colors ${
                                orderFilter === status 
                                ? 'bg-black text-white' 
                                : 'bg-white text-gray-600 hover:bg-gray-100'
                            }`}
                        >
                            {status}
                        </button>
                    ))}
                </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                        <tr>
                            <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Order ID</th>
                            <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Customer</th>
                            <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Date</th>
                            <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Status</th>
                            <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Total</th>
                            <th className="px-6 py-4 text-right text-xs font-bold text-gray-500 uppercase tracking-wider">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {[...safeOrders].filter(o => orderFilter==='All' || o.status===orderFilter).sort((a,b)=>new Date(b.date).getTime()-new Date(a.date).getTime()).map(order => (
                            <tr key={order.id} className="hover:bg-gray-50 transition-colors">
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-blue-600">#{order.id}</td>
                                <td className="px-6 py-4">
                                    <div className="text-sm font-medium text-gray-900">{order.customerName}</div>
                                    <div className="text-xs text-gray-500">{order.customerPhone}</div>
                                </td>
                                <td className="px-6 py-4 text-sm text-gray-500">
                                    {new Date(order.date).toLocaleDateString()} <br/>
                                    <span className="text-xs text-gray-400">{new Date(order.date).toLocaleTimeString()}</span>
                                </td>
                                <td className="px-6 py-4">
                                    <select 
                                        value={order.status}
                                        onChange={(e) => onUpdateOrder(order.id, { status: e.target.value as OrderStatus })}
                                        className={`text-xs font-bold px-2 py-1 rounded-full border-0 cursor-pointer ${
                                            order.status === 'Pending' ? 'bg-yellow-100 text-yellow-800' :
                                            order.status === 'Approved' ? 'bg-blue-100 text-blue-800' :
                                            order.status === 'Delivered' ? 'bg-green-100 text-green-800' :
                                            order.status === 'Declined' ? 'bg-red-100 text-red-800' :
                                            'bg-gray-100 text-gray-800'
                                        }`}
                                    >
                                        <option value="Pending">Pending</option>
                                        <option value="Approved">Approved</option>
                                        <option value="Processing">Processing</option>
                                        <option value="Shipped">Shipped</option>
                                        <option value="Delivered">Delivered</option>
                                        <option value="Declined">Declined</option>
                                    </select>
                                </td>
                                <td className="px-6 py-4 text-sm font-bold text-gray-900">
                                    Rs. {order.total.toLocaleString()}
                                </td>
                                <td className="px-6 py-4 text-right text-sm font-medium">
                                    <button onClick={() => setSelectedOrder(order)} className="text-gray-400 hover:text-blue-600 transition-colors"><Eye size={20} /></button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
           </div>
        )}

        {activeTab === 'products' && (
          <div>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-gray-800">Product Inventory</h2>
              <button onClick={handleAddProductClick} className="bg-black text-white px-6 py-2 rounded-lg flex items-center gap-2 hover:bg-gray-800 transition-all shadow-lg hover:shadow-xl">
                <Plus size={20} /> Add New Product
              </button>
            </div>
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase">Details</th>
                    <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase">Pricing</th>
                    <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase">Stock</th>
                    <th className="px-6 py-3 text-right text-xs font-bold text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {products.map((product) => (
                    <tr key={product.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div className="flex items-center">
                          <img className="h-12 w-12 rounded-lg object-cover bg-gray-100 border" src={product.images?.[0]} onError={(e) => (e.target as HTMLImageElement).src='https://placehold.co/100'} />
                          <div className="ml-4">
                            <div className="text-sm font-bold text-gray-900">{product.name}</div>
                            <div className="text-xs text-gray-500 bg-gray-100 inline-block px-2 py-0.5 rounded mt-1">{product.category}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900 font-medium">
                          Rs. {product.price}
                          {product.discountPrice && <div className="text-xs text-red-500 line-through">Rs. {product.discountPrice}</div>}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                          <span className={`px-2 py-1 rounded text-xs font-bold ${product.stock > 0 ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                              {product.stock > 0 ? `${product.stock} in stock` : 'Out of Stock'}
                          </span>
                      </td>
                      <td className="px-6 py-4 text-right text-sm font-medium">
                        <button onClick={() => handleEditProduct(product)} className="text-blue-600 hover:text-blue-900 mr-4"><Edit size={18} /></button>
                        <button onClick={() => { if(confirm('Delete?')) onDeleteProduct(product.id) }} className="text-red-600 hover:text-red-900"><Trash2 size={18} /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
        
        {activeTab === 'dashboard' && (
             <div className="space-y-8">
                 <div className="flex justify-between items-center">
                    <h2 className="text-xl font-bold">Performance Overview</h2>
                    <div className="flex bg-white rounded-lg p-1 border">
                        {['all', 'today', 'week', 'month'].map(t => (
                            <button 
                                key={t}
                                onClick={() => setDashboardTimeFilter(t as any)}
                                className={`px-4 py-1.5 text-xs font-bold uppercase rounded-md transition-colors ${dashboardTimeFilter===t ? 'bg-black text-white' : 'text-gray-500 hover:bg-gray-50'}`}
                            >
                                {t}
                            </button>
                        ))}
                    </div>
                 </div>

                 <div className="grid grid-cols-4 gap-6">
                     <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
                        <div className="flex justify-between items-start mb-4">
                            <div className="p-3 bg-blue-50 rounded-xl"><DollarSign className="text-blue-600" size={24}/></div>
                            <span className="text-xs font-bold text-green-500 bg-green-50 px-2 py-1 rounded">Total</span>
                        </div>
                        <h3 className="text-gray-500 text-sm font-medium">Revenue</h3>
                        <p className="text-3xl font-bold text-gray-900 mt-1">Rs. {(filterOrdersByTime(safeOrders, dashboardTimeFilter).reduce((a,c) => a + (c.total||0), 0)).toLocaleString()}</p>
                     </div>
                     
                     <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
                        <div className="flex justify-between items-start mb-4">
                            <div className="p-3 bg-purple-50 rounded-xl"><ShoppingBag className="text-purple-600" size={24}/></div>
                        </div>
                        <h3 className="text-gray-500 text-sm font-medium">Total Orders</h3>
                        <p className="text-3xl font-bold text-gray-900 mt-1">{filterOrdersByTime(safeOrders, dashboardTimeFilter).length}</p>
                     </div>

                      <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
                        <div className="flex justify-between items-start mb-4">
                            <div className="p-3 bg-yellow-50 rounded-xl"><AlertTriangle className="text-yellow-600" size={24}/></div>
                        </div>
                        <h3 className="text-gray-500 text-sm font-medium">Pending Orders</h3>
                        <p className="text-3xl font-bold text-gray-900 mt-1">{pendingOrdersCount}</p>
                     </div>
                 </div>
             </div>
        )}

        {/* Categories Tab */}
        {activeTab === 'categories' && (
            <div className="max-w-2xl">
                 <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200 mb-6">
                    <h3 className="font-bold mb-4">Add Category</h3>
                    <form onSubmit={(e) => {
                         e.preventDefault(); 
                         if(newCategoryName.trim()) { onAddCategory({id: Date.now().toString(), name: newCategoryName}); setNewCategoryName(''); }
                    }} className="flex gap-2">
                        <input className="flex-1 border-2 border-gray-200 p-3 rounded-lg focus:border-black outline-none" placeholder="Category Name" value={newCategoryName} onChange={e=>setNewCategoryName(e.target.value)} />
                        <button className="bg-black text-white px-6 py-3 rounded-lg font-bold hover:bg-gray-800">Add</button>
                    </form>
                 </div>
                 <div className="bg-white rounded-xl shadow-sm border border-gray-200 divide-y">
                     {categories.map(c => (
                         <div key={c.id} className="p-4 flex justify-between items-center">
                             <span className="font-medium">{c.name}</span>
                             <button onClick={()=>onDeleteCategory(c.id)} className="text-red-500 p-2 hover:bg-red-50 rounded"><Trash2 size={18}/></button>
                         </div>
                     ))}
                 </div>
            </div>
        )}

      </main>

      {/* --- ORDER DETAILS MODAL --- */}
      {selectedOrder && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-[70] flex items-center justify-center p-4 backdrop-blur-sm">
              <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl">
                  <div className="p-6 border-b flex justify-between items-center bg-gray-50">
                      <div>
                          <h3 className="text-xl font-bold">Order #{selectedOrder.id}</h3>
                          <p className="text-sm text-gray-500">{new Date(selectedOrder.date).toLocaleString()}</p>
                      </div>
                      <button onClick={() => setSelectedOrder(null)} className="p-2 hover:bg-gray-200 rounded-full"><X size={24}/></button>
                  </div>
                  <div className="p-6 space-y-6">
                      <div className="grid grid-cols-2 gap-6">
                          <div className="p-4 bg-blue-50 rounded-xl border border-blue-100">
                              <h4 className="font-bold text-blue-900 mb-2 flex items-center gap-2"><Truck size={16}/> Customer Info</h4>
                              <p className="font-bold">{selectedOrder.customerName}</p>
                              <p>{selectedOrder.customerPhone}</p>
                              <p className="text-sm text-gray-600 mt-1">{selectedOrder.address}</p>
                          </div>
                          <div className="p-4 bg-green-50 rounded-xl border border-green-100">
                               <h4 className="font-bold text-green-900 mb-2 flex items-center gap-2"><DollarSign size={16}/> Payment</h4>
                               <p className="font-bold">{selectedOrder.paymentMethod}</p>
                               <div className="mt-2 text-sm">
                                   <p>Subtotal: Rs. {selectedOrder.subtotal?.toLocaleString() || selectedOrder.total.toLocaleString()}</p>
                                   {selectedOrder.discount > 0 && <p className="text-green-600">Discount: -Rs. {selectedOrder.discount.toLocaleString()}</p>}
                                   <p className="text-xl font-bold mt-1">Total: Rs. {selectedOrder.total.toLocaleString()}</p>
                               </div>
                          </div>
                      </div>

                      <div>
                          <h4 className="font-bold mb-3">Order Items</h4>
                          <div className="border rounded-xl overflow-hidden">
                              <table className="w-full text-sm text-left">
                                  <thead className="bg-gray-50">
                                      <tr>
                                          <th className="px-4 py-2">Item</th>
                                          <th className="px-4 py-2">Size/Color</th>
                                          <th className="px-4 py-2 text-right">Qty</th>
                                          <th className="px-4 py-2 text-right">Price</th>
                                      </tr>
                                  </thead>
                                  <tbody>
                                      {selectedOrder.items.map((item, idx) => (
                                          <tr key={idx} className="border-t">
                                              <td className="px-4 py-3 font-medium">{item.name}</td>
                                              <td className="px-4 py-3 text-gray-500">{item.selectedSize} / {item.selectedColor}</td>
                                              <td className="px-4 py-3 text-right">{item.quantity}</td>
                                              <td className="px-4 py-3 text-right">Rs. {((item.discountPrice||item.price)*item.quantity).toLocaleString()}</td>
                                          </tr>
                                      ))}
                                  </tbody>
                              </table>
                          </div>
                      </div>
                      
                      <div className="flex gap-3 pt-4 border-t">
                          <button onClick={() => { onUpdateOrder(selectedOrder.id, {status: 'Approved'}); setSelectedOrder(null); }} className="flex-1 bg-blue-600 text-white py-3 rounded-xl font-bold hover:bg-blue-700">Approve Order</button>
                          <button onClick={() => { onUpdateOrder(selectedOrder.id, {status: 'Declined'}); setSelectedOrder(null); }} className="flex-1 bg-red-100 text-red-700 py-3 rounded-xl font-bold hover:bg-red-200">Decline</button>
                      </div>
                  </div>
              </div>
          </div>
      )}

      {/* --- PRODUCT MODAL --- */}
      {isProductModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-[60] flex items-center justify-center p-4 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
             <div className="p-6 border-b flex justify-between items-center sticky top-0 bg-white z-10">
                 <h3 className="text-xl font-bold">{editingProduct ? 'Edit Product' : 'Add New Product'}</h3>
                 <button onClick={() => setIsProductModalOpen(false)} className="p-2 hover:bg-gray-100 rounded-full"><X size={24} /></button>
             </div>
             
             <form onSubmit={saveProduct} className="p-6 space-y-5">
                 <div className="grid grid-cols-2 gap-4">
                    <div>
                        <label className="block text-xs font-bold uppercase text-gray-500 mb-1">Product Name</label>
                        <input required className="w-full border-2 border-gray-200 p-3 rounded-lg focus:border-black outline-none transition-colors" value={productForm.name} onChange={e=>setProductForm({...productForm, name: e.target.value})} placeholder="e.g. Nike Air Max"/>
                    </div>
                    <div>
                        <label className="block text-xs font-bold uppercase text-gray-500 mb-1">Category</label>
                        <select className="w-full border-2 border-gray-200 p-3 rounded-lg focus:border-black outline-none" value={productForm.category} onChange={e=>setProductForm({...productForm, category: e.target.value})}>
                            {categories.length>0 ? categories.map(c=><option key={c.id} value={c.name}>{c.name}</option>) : <option>Men</option>}
                        </select>
                    </div>
                 </div>

                 <div className="grid grid-cols-3 gap-4">
                    <div><label className="block text-xs font-bold uppercase text-gray-500 mb-1">Price</label><input required type="number" className="w-full border-2 border-gray-200 p-3 rounded-lg" value={productForm.price} onChange={e=>setProductForm({...productForm, price: Number(e.target.value)})}/></div>
                    <div><label className="block text-xs font-bold uppercase text-gray-500 mb-1">Stock</label><input required type="number" className="w-full border-2 border-gray-200 p-3 rounded-lg" value={productForm.stock} onChange={e=>setProductForm({...productForm, stock: Number(e.target.value)})}/></div>
                    <div><label className="block text-xs font-bold uppercase text-gray-500 mb-1">Discount Price</label><input type="number" className="w-full border-2 border-gray-200 p-3 rounded-lg" value={productForm.discountPrice || ''} onChange={e=>setProductForm({...productForm, discountPrice: Number(e.target.value)})}/></div>
                 </div>

                 <div>
                    <label className="block text-xs font-bold uppercase text-gray-500 mb-1">Description</label>
                    <textarea className="w-full border-2 border-gray-200 p-3 rounded-lg h-24" value={productForm.description} onChange={e=>setProductForm({...productForm, description: e.target.value})}/>
                 </div>
                 
                 {/* SIMPLIFIED IMAGE UPLOAD */}
                 <div className="bg-gray-50 p-5 rounded-xl border-2 border-dashed border-gray-300">
                    <label className="block text-sm font-bold mb-3">Product Images</label>
                    
                    <div className="grid grid-cols-4 gap-3 mb-4">
                        {existingImages.map((img, i) => (
                            <div key={`exist-${i}`} className="relative aspect-square rounded-lg overflow-hidden border bg-white group">
                                <img src={img} className="w-full h-full object-cover" />
                                <button type="button" onClick={()=>setExistingImages(p=>p.filter((_,x)=>x!==i))} className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"><X size={12}/></button>
                            </div>
                        ))}
                        {selectedImages.map((f, i) => (
                            <div key={`new-${i}`} className="relative aspect-square rounded-lg overflow-hidden border bg-blue-50 group flex items-center justify-center">
                                <span className="text-[10px] text-center p-1 text-blue-800 break-all">{f.name.slice(0,10)}..</span>
                                <button type="button" onClick={()=>setSelectedImages(p=>p.filter((_,x)=>x!==i))} className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"><X size={12}/></button>
                            </div>
                        ))}
                        
                        {/* THE BIG ADD BUTTON */}
                        <label className="cursor-pointer aspect-square rounded-lg border-2 border-dashed border-blue-300 bg-blue-50 hover:bg-blue-100 flex flex-col items-center justify-center text-blue-600 transition-colors">
                            <Plus size={24} />
                            <span className="text-xs font-bold mt-1">Add Image</span>
                            <input 
                                ref={fileInputRef}
                                type="file" 
                                accept="image/*" 
                                onChange={handleFileSelect} 
                                className="hidden" 
                            />
                        </label>
                    </div>
                
                 </div>

                 <div className="flex gap-3 pt-4 border-t">
                     <button type="button" onClick={() => setIsProductModalOpen(false)} className="flex-1 bg-gray-100 py-3 rounded-xl font-bold hover:bg-gray-200">Cancel</button>
                     <button type="submit" disabled={isSubmitting} className="flex-1 bg-black text-white py-3 rounded-xl font-bold hover:bg-gray-800 disabled:opacity-50 flex items-center justify-center gap-2">
                         {isSubmitting ? <><Loader2 className="animate-spin" size={20}/> Saving...</> : 'Save Product'}
                     </button>
                 </div>
             </form>
          </div>
        </div>
      )}
    </div>
  );
};